import { useCallback } from 'react'
import { withoutLocaleParam } from '@/lib/routes'
import { useStoreSafe } from '@/lib/hooks/useStoreSafe'
import { ComponentPath, GlobImport } from '../types'
import { visitOptionsHook } from './inertiaConfig'

class PageConfigs {
  private byComponent!: Record<string, EnhancedPageConfig>
  private byUrl!: Record<string, EnhancedPageConfig>
  private byRegExp!: Array<EnhancedPageConfig>

  init({
    pageConfigs,
    layoutConfigs,
  }: {
    pageConfigs: GlobImport<PageConfig>
    layoutConfigs: GlobImport<LayoutConfig>
  }) {
    const configs = this.transformConfigs({ ...pageConfigs, ...layoutConfigs })
    this.byComponent = Object.fromEntries(configs.map(([key, value]) => [key, value]))
    this.byUrl = Object.fromEntries(configs.map(([key, value]) => [value.url || `/${key}`, value]))
    this.byRegExp = configs.filter(([_, value]) => value.urlRegExp).map(([_, value]) => value)
  }

  getPageConfig(componentPath: ComponentPath) {
    if (componentPath.component) {
      return this.byComponent[componentPath.component]
    }

    if (componentPath.url) {
      const rawHref = withoutLocaleParam(componentPath.url).split('?')[0]
      if (this.byUrl[rawHref]) {
        return this.byUrl[rawHref]
      }

      // TODO: maybe use `URLPattern`
      for (const config of this.byRegExp) {
        if (config.urlRegExp?.test(rawHref)) {
          return config
        }
      }
    }
  }

  getEnhancedPageConfig(componentPath: ComponentPath) {
    return this.getPageConfig(componentPath) || noopEnhancedPageConfig
  }

  private transformConfigs(configs: GlobImport<PageConfig>) {
    return Object.entries(configs).map(([key, pageConfig]) => [
      key.replace(/^..\/..\/pages\/(.+?)\/(Page|Layout)\.config\..+$/m, '$1'),
      this.enhancePageConfig(pageConfig),
    ]) as Array<[string, EnhancedPageConfig]>
  }

  private enhancePageConfig(pageConfig: PageConfig): EnhancedPageConfig {
    return {
      usePageConfig: (action, deps = []) =>
        useStoreSafe(
          pageConfig.store,
          // eslint-disable-next-line react-hooks/exhaustive-deps
          useCallback((state: any) => action(state), deps),
        ),
      call: action => action(pageConfig.store?.getState()),
      ...pageConfig,
    }
  }
}

const noopEnhancedPageConfig: EnhancedPageConfig = {
  usePageConfig: (action, deps = []) =>
    useStoreSafe(
      undefined,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useCallback((state: any) => action(state), deps),
    ),
  call: action => action(undefined),
}

export const pageConfigs = new PageConfigs()

const subscriptions = [
  visitOptionsHook((href, options) => {
    const pageConfig = pageConfigs.getPageConfig({ url: href })

    return (
      pageConfig?.call(state => pageConfig?.visitOptionsHook?.(href, options, state)) || options
    )
  }),
]

if (import.meta.env.DEV && typeof window !== 'undefined') {
  const __dev = ((window as any).__dev ||= {})
  __dev.pageConfigs = pageConfigs
  __dev[import.meta.url]?.subscriptions?.forEach?.((unsubscribe: () => void) => unsubscribe())
  __dev[import.meta.url] = { subscriptions }
}
